import React, { useCallback, memo, useContext, useState } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { addItem, addSubItem } from 'Shared/actions/todos';
import { jsonEmptyContentState, jsonContentStateFromString } from 'Shared/utils/draftJsHelpers';
import AddItemButtons from 'Shared/components/AddItemButtons/AddItemButtons';
import WorkspaceTemplatesButton from 'Shared/components/WorkspaceTemplatesButton/WorkspaceTemplatesButton';
import { ItemType } from 'Shared/constants/types';
import VideoRecorder from 'Shared/components/VideoRecorder/VideoRecorder';
import MobileContext from 'Shared/contexts/MobileContext';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import { useToggle, useQueryParam } from 'Shared/utils/hooks';
import { useWorkspace } from 'Shared/utils/hooks/use-workspace';

import QueryParamConstants from 'Shared/constants/QueryParams.constants';
import TemplateGalleryModal from './TemplateGalleryModal/TemplateGalleryModal';
import SaveWorkspaceTemplateModal from './SaveWorkspaceTemplateModal/SaveWorkspaceTemplateModal';
import StartMeetingModeButton from 'Dashboard/components/WorkspacePage/partials/StartMeetingModeButton/StartMeetingModeButton';
import MeetingPanel from 'Dashboard/components/WorkspacePage/partials/MeetingPanel/MeetingPanel';

import './DiscussionTopicsCreatePanel.scss';

type Props = {
  onAddItem: Function;
  onSelectedItemUpdate: Function;
  isEmpty?: boolean;
  isZoom?: boolean;
};

function DiscussionTopicsCreatePanel({ onAddItem, onSelectedItemUpdate, isEmpty, isZoom }: Props) {
  const dispatch = useDispatch();
  const { id: workspaceId, isGroup } = useWorkspace();

  const [isShowVideoRecorder, toggleIsVideoRecorder] = useToggle(false);
  const [showModal, setShowModal] = useQueryParam(QueryParamConstants.TEMPLATES_MODAL);
  const [isSaveWorkspaceTemplateModalOpen, setIsSaveWorkspaceTemplateModalOpen] = useState(false);

  const isMobile = useContext(MobileContext);

  // 4/26/21 - We are now always going to show the templates button regardless of the feature flag setting.
  // The feature flag will still control whether orgs get the mandatory choose a template screen
  const showTemplatesButton = isEmpty && isZoom ? false : true;

  const handleVideoItemAdd = useCallback(
    video => {
      const body = jsonContentStateFromString(video.audioOnly ? 'New Audio Item' : 'New Video Item');
      const itemUuid = uuidv4();
      dispatch(
        addItem({
          itemUuid,
          body,
          workspaceId,
          video,
          position: 1,
          itemType: ItemType.discussionTopic,
          isAssignCurrentUser: true,
        })
      );
    },
    [dispatch, workspaceId]
  );

  const handleAddSubItem = useCallback(() => {
    const body = jsonEmptyContentState();
    const itemUuid = uuidv4();
    dispatch(addSubItem({ itemUuid, body, workspaceId, itemType: ItemType.discussionTopic }));

    onSelectedItemUpdate({ itemUuid });
  }, [dispatch, workspaceId, onSelectedItemUpdate]);

  const handleAddSection = useCallback(() => {
    const body = jsonEmptyContentState();
    onAddItem({ isSection: true, jsonBody: body, isAssignCurrentUser: false });
  }, [onAddItem]);

  const handleSaveWorkspaceTemplateButtonClick = useCallback(() => setIsSaveWorkspaceTemplateModalOpen(true), []);
  const handleSaveWorkspaceTemplateButtonToggle = useCallback(
    () => setIsSaveWorkspaceTemplateModalOpen(state => !state),
    []
  );

  const showAddtopicButton = !isZoom || (isZoom && !isEmpty);

  const baseClassName = 'DiscussionTopicsCreatePanel';

  return (
    <>
      {isShowVideoRecorder && <VideoRecorder onSave={handleVideoItemAdd} onHide={toggleIsVideoRecorder} />}
      {showModal && (
        <TemplateGalleryModal
          onToggle={() => setShowModal(null)}
          isGroup={isGroup}
          onCreateTemplate={() => {
            handleSaveWorkspaceTemplateButtonClick();
            setShowModal(null);
          }}
        />
      )}
      {isSaveWorkspaceTemplateModalOpen && (
        <SaveWorkspaceTemplateModal onToggle={handleSaveWorkspaceTemplateButtonToggle} />
      )}
      <div className={baseClassName}>
        <div className={`${baseClassName}__title-bar`}>
          <div className="">
            <h4 className={`${baseClassName}__title-bar__title`}>Agenda</h4>
          </div>
          <div className={`${baseClassName}__title-bar__btn-area`}>
            <WpButtonGroup>
              {showTemplatesButton && !isMobile && (
                <WorkspaceTemplatesButton
                  onAddTemplate={() => setShowModal('workspace')}
                  onCreateTemplate={handleSaveWorkspaceTemplateButtonClick}
                />
              )}
              {showAddtopicButton && !isMobile && (
                <AddItemButtons
                  isDiscussionTopicOption
                  onAddMeetingItem={onAddItem}
                  onAddSubItem={handleAddSubItem}
                  onAddSection={handleAddSection}
                  onVideoRecorderToggle={toggleIsVideoRecorder}
                />
              )}
              {(!isZoom || (isZoom && !isMobile)) && <StartMeetingModeButton />}
            </WpButtonGroup>
          </div>
        </div>
        <MeetingPanel />
        {isMobile && (
          <AddItemButtons
            block
            isDiscussionTopicOption
            onAddMeetingItem={onAddItem}
            onAddSubItem={handleAddSubItem}
            onAddSection={handleAddSection}
            onVideoRecorderToggle={toggleIsVideoRecorder}
          />
        )}
      </div>
    </>
  );
}

export default memo(DiscussionTopicsCreatePanel);
